/**
 * Витрина живёт в том же Nuxt-приложении, страницы — в `pages/shop/`.
 * На боевом `app/router.options.ts` отдаёт их с поддомена (`shop.vetsy.ru`) без префикса.
 * На локали поддомена нет: те же страницы открываются на основном хосте с `/katalog`.
 */
export const SHOP_ROUTE_PREFIX = '/shop'

/** Префикс витрины на локальном основном хосте (`http://vetsy.test/katalog`). */
export const SHOP_LOCAL_PREFIX = '/katalog'

/** Catch-all витрины `pages/shop/[...rest].vue`: уводит адреса основного сайта на его домен. */
export const SHOP_MAIN_SITE_ROUTE = '/:rest(.*)*'

export function resolveShopDomain(shopDomain: string | undefined, mainDomain: string | undefined): string {
    return (shopDomain || `shop.${mainDomain || 'vetsy.ru'}`).toLowerCase()
}

export function isShopHostname(host: string | undefined, shopDomain: string): boolean {
    return (host ?? '').toLowerCase() === shopDomain
}

/**
 * Браузер на витрине ходит в API того же хоста. Прод собирается с абсолютным
 * `https://vetsy.ru/api/v1`, и с `shop.vetsy.ru` это чужой origin. Nginx витрины
 * отдаёт `/api` в Laravel так же, как основной сайт, поэтому берём только путь.
 */
export function shopClientApiBase(host: string | undefined, shopDomain: string, configuredBase: string): string {
    if (!isShopHostname(host, shopDomain) || configuredBase.startsWith('/')) {
        return configuredBase
    }

    try {
        const path = new URL(configuredBase).pathname

        return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
    } catch {
        return configuredBase
    }
}

export function stripShopPrefix(path: string): string {
    if (path === SHOP_ROUTE_PREFIX) {
        return '/'
    }

    return path.startsWith(`${SHOP_ROUTE_PREFIX}/`) ? path.slice(SHOP_ROUTE_PREFIX.length) : path
}

export function isShopRoutePath(path: string): boolean {
    return path === SHOP_ROUTE_PREFIX || path.startsWith(`${SHOP_ROUTE_PREFIX}/`)
}

/** Локальный основной хост: витрина на `/katalog`, а не на поддомене. */
export function shopUsesPathOnMainSite(mainDomain: string | undefined): boolean {
    const host = (mainDomain ?? '').toLowerCase().split(':')[0]

    return host === 'localhost' || host.endsWith('.test') || host.endsWith('.local')
}

/**
 * Путь страницы `pages/shop/` на локальном основном сайте.
 * Корень витрины — `/katalog` (сама страница каталога). Главная сайта остаётся `/`.
 * Catch-all, который уводит разделы основного сайта на его домен, локально не нужен.
 */
export function mainSiteShopPath(routePath: string): string | null {
    if (!isShopRoutePath(routePath) || routePath.includes(':rest')) {
        return null
    }

    const stripped = stripShopPrefix(routePath)

    if (stripped === '/' || stripped === '') {
        return null
    }

    if (/^\/katalog\/?$/.test(stripped)) {
        return SHOP_LOCAL_PREFIX
    }

    return `${SHOP_LOCAL_PREFIX}${stripped}`
}

/** Публичный адрес витрины: на локали к корню поддомена добавляется `/katalog`. */
export function prefixShopPublicPath(path: string, prefix: string): string {
    if (!prefix || path === prefix || path.startsWith(`${prefix}/`)) {
        return path
    }

    return `${prefix}${path.startsWith('/') ? path : `/${path}`}`
}

export function isLocalShopPath(path: string): boolean {
    return path === SHOP_LOCAL_PREFIX || path.startsWith(`${SHOP_LOCAL_PREFIX}/`)
}

/** Статические первые сегменты маршрутов основного сайта: `/clinics/:city()` → `clinics`. */
export function mainSiteSections(paths: string[]): string[] {
    const sections = paths
        .map((path) => path.split('/')[1] ?? '')
        .filter((segment) => /^[a-z0-9-]+$/i.test(segment))

    return [...new Set(sections)]
}
